import React from 'react';
import styled from '@emotion/styled';
import type { StayFull } from '../../../../interfaces/stays';
import { stayJourney } from '../../../../utils/stays-stage';
import { duration, easing, palette, radius, space, spacing } from '../../../../style-utils/theme';

/**
 * A STAY'S JOURNEY BAR (plan S4, R-S4.25): how far through its stay a booking
 * is, on the BUILDING's wall clock (`building_now` from the envelope — never
 * the device's hour, the Konios bug). The server decides the stage; the bar is
 * only its picture, and the words ride in aria-valuetext so the bar is never
 * colour alone.
 */

const Track = styled.div`
  position: relative;
  width: 100%;
  max-width: ${space(20)};
  height: ${spacing.half};
  margin-top: ${spacing.half};
  border-radius: ${radius.pill};
  background: ${palette.surfaceMuted};
  overflow: hidden;
`;

const Fill = styled.span`
  position: absolute;
  inset: 0 auto 0 0;
  border-radius: ${radius.pill};
  background: ${palette.iris};
  transition: width ${duration.base} ${easing.standard};
`;

export interface JourneyBarProps {
  stay: Pick<StayFull, 'stage' | 'nights' | 'effective'>;
  /** The envelope's building wall clock, 'YYYY-MM-DDTHH:mm'. */
  buildingNow: string;
  /** The guest's name, for the bar's accessible name. */
  name: string;
}

export const JourneyBar: React.FC<JourneyBarProps> = ({ stay, buildingNow, name }) => {
  const { pct, text } = stayJourney(stay, buildingNow);
  const value = Math.max(0, Math.min(100, Math.round(pct)));

  return (
    <Track
      role="progressbar"
      aria-label={`Stay progress for ${name}`}
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuetext={text}
      data-stage={stay.stage.key}
    >
      <Fill style={{ width: `${value}%` }} />
    </Track>
  );
};
